/*
 * Shade family filter for the product page swatch grid.
 *
 * Picking a shade makes the theme's <variant-picker> re-fetch the section and replace itself, which destroys any state
 * held on the element. This is a custom element rather than a delegated listener so that connectedCallback runs on the
 * replacement node and restores the active family before the browser paints, avoiding a flash of the full shade range.
 *
 * State lives in this module, not in sessionStorage, so it outlives that replacement but still resets to "all" on a
 * real page load: a shopper should never return to a PDP and silently see a filtered subset of the range.
 *
 * Hiding rules, which must not fight the Liquid that renders the swatches:
 *   - the <input> owns the `hidden` attribute. Liquid never writes it there (it writes `disabled`), and `hidden` takes
 *     the swatch out of the tab order.
 *   - the label/anchor owns the `is-filtered-out` class. Liquid does write `hidden` onto the anchor for combined
 *     products, so clearing that attribute here would resurrect swatches Liquid deliberately hid.
 */
(function () {
  var ALL = 'all';
  var activeFamilies = new Map();

  var ShadeFamilyFilter = class extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this._onClick.bind(this));

      var restored = activeFamilies.get(this.stateKey);

      this.apply(restored && this.pillFor(restored) ? restored : ALL);
    }

    get stateKey() {
      return this.getAttribute('state-key') || '';
    }

    get pills() {
      return Array.from(this.querySelectorAll('.shade-families__pill'));
    }

    /* Scoped to the value list because the pills carry data-shade-family too. */
    get swatches() {
      return Array.from(this.querySelectorAll('.variant-picker__option-values [data-shade-family]'));
    }

    pillFor(family) {
      return this.querySelector('.shade-families__pill[data-shade-family="' + family + '"]');
    }

    _onClick(event) {
      var pill = event.target.closest('.shade-families__pill');

      if (!pill || !this.contains(pill)) return;

      var family = pill.dataset.shadeFamily;

      activeFamilies.set(this.stateKey, family);
      this.apply(family);
    }

    apply(family) {
      this.pills.forEach(function (pill) {
        var isActive = pill.dataset.shadeFamily === family;

        pill.classList.toggle('is-active', isActive);
        pill.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      this.swatches.forEach(function (element) {
        var filtered = family !== ALL && element.dataset.shadeFamily !== family;

        if (element.tagName === 'INPUT') {
          element.toggleAttribute('hidden', filtered);
        } else {
          element.classList.toggle('is-filtered-out', filtered);
        }
      });
    }
  };

  if (!window.customElements.get('shade-family-filter')) {
    window.customElements.define('shade-family-filter', ShadeFamilyFilter);
  }
})();
